import { UserAvatar } from '@/components'
import CustomTimeAgo from '@/components/CustomTimeAgo'
import { useLocalStorage } from '@/hooks/common'
import { CBox, RBox } from '@/pages/__styled'
import colors from '@/styles/colors'
import {
  ActivityTypeUtil,
  NotificationGroup,
  NotificationItem,
  SocketEventType,
} from '@/types/notification'
import { TopicTypes } from '@/utils/constants'
import { convertToGMT } from '@/utils/helper'
import { Box, styled, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'

type Variant = 'list' | 'popup'
interface NotificationItemCardProps {
  item: NotificationGroup
  onClick: (item: NotificationItem) => void
  variant?: Variant
  hidePopMenu?: () => void
}

interface StyledBoxProps {
  variant: Variant
}

const CardContainer = styled(Box)<StyledBoxProps>(({ theme, variant }) => ({
  display: 'flex',
  flexDirection: 'column',

  '& .commentContainer': {
    marginTop: '8px',
    marginLeft: '40px',
    backgroundColor: '#F7F7F7',
    padding: '18px 20px',
    borderRadius: '8px',
  },

  ...(variant === 'list' && {
    '& .sender': {
      fontSize: '13px',
      fontWeight: '600',
      color: colors.themeBlack,
    },
    '& .topicTitle': {
      fontSize: '13px',
      fontFamily: 'Inter-Medium',
      color: colors.themeBlack,
    },
    '& .activity': {
      fontSize: '13px',
      fontWeight: '300',
      color: colors.darkGrey,
    },
    '& .dateTime': {
      fontSize: '10px',
      fontWeight: '400',
      color: colors.themePurpleTextColor,
    },
    '& .commentText': {
      fontSize: '13px',
      fontWeight: '400',
      style: 'italic',
      color: colors.themeBlack,
    },
  }),
  ...(variant === 'popup' && {
    '& .sender': {
      fontSize: '13px',
      fontWeight: '600',
      color: colors.themeBlack,
    },
    '& .topicTitle': {
      fontSize: '13px',
      fontFamily: 'Inter-Medium',
      color: colors.themeBlack,
    },
    '& .activity': {
      fontSize: '13px',
      fontWeight: '300',
      color: colors.darkGrey,
    },
    '& .dateTime': {
      fontSize: '10px',
      fontWeight: '400',
      color: colors.themePurpleTextColor,
    },
    '& .commentText': {
      fontSize: '13px',
      fontWeight: '400',
      style: 'italic',
      color: colors.themeBlack,
    },
  }),
}))

const NotificationItemCard = ({
  item,
  variant = 'popup',
  onClick,
  hidePopMenu,
}: NotificationItemCardProps) => {
  const navigate = useNavigate()
  const displayName = `${item.sourceUserFullName}`

  const countText = item.count > 1 ? ` (${item.count})` : ''
  const activityPrefix = ActivityTypeUtil.getActivityText(item)
  const activityText = ActivityTypeUtil.isTopicRelatedEvent(item.eventType)
    ? activityPrefix
    : `${activityPrefix} ${countText}`
  const topicTitle = ActivityTypeUtil.isTopicRelatedEvent(item.eventType)
    ? `${ActivityTypeUtil.getTopicTitle(item)} ${countText}`
    : ''

  const onClickAvatar = (e: React.MouseEvent) => {
    e.stopPropagation()
    hidePopMenu?.()
    let previewQuery = new URLSearchParams()
    previewQuery.set('id', item.sourceUserID)
    navigate(`/profile/user?${previewQuery.toString()}`)
  }

  return (
    <CardContainer variant={variant} onClick={() => onClick(item)} sx={{ cursor: 'pointer' }}>
      <RBox>
        <Box
          onClick={onClickAvatar}
          sx={{
            position: 'relative',
            cursor: 'pointer',
          }}
        >
          <UserAvatar
            borderRadius={100}
            name={displayName}
            url={item.profilePicThumbURL}
            size={35}
          />
        </Box>
        <Typography mx={1} sx={{ flex: 1 }}>
          <Typography onClick={onClickAvatar} className={'sender'} component='span'>
            {`${displayName} `}
          </Typography>
          <Typography className={'activity'} component='span'>
            {`${activityText} `}
          </Typography>
          {ActivityTypeUtil.isSocketEventSupported(item.eventType) && (
            <Typography className={'topicTitle'} component='span'>
              {`${topicTitle} `}
            </Typography>
          )}
        </Typography>
        <Typography className={'dateTime'} component='span'>
          <CustomTimeAgo date={item.eventDateTime} />
        </Typography>
      </RBox>

      {item.eventType == SocketEventType.NOTE_COMMENT_ADDED && (
        <Box sx={{ display: 'flex' }}>
          <Box className={'commentContainer'} width={'auto'}>
            <Typography className={'commentText'}>
              {ActivityTypeUtil.getCommentText(item)}
            </Typography>
          </Box>
        </Box>
      )}
    </CardContainer>
  )
}

export default NotificationItemCard
